// Pure selection state for the Lighter multi-subaccount picker. Each entry
// is one subaccount slot the customer has chosen to link, plus however far
// its key has been resolved. No side effects here — the hook wrapper in
// use-lighter-onboard.ts owns the network calls (list/prepare/confirm/link).
//
// `accountIndex` is a decimal string (proto int64 fields decode as strings,
// not bigint or number — see LighterSubaccountInfo.accountIndex).

export type KeySource =
  | { type: 'none' }
  | { type: 'preparing' }
  | { type: 'prepared'; preparedKeyId: string; personalSignText: string }
  | { type: 'confirming'; preparedKeyId: string }
  | { type: 'confirmed'; preparedKeyId: string }

export interface SlotSelection {
  accountIndex: string
  apiKeyIndex: number
  keySource: KeySource
}

export interface SelectionState {
  slots: SlotSelection[]
}

export function empty(): SelectionState {
  return { slots: [] }
}

// The Lighter API-key slot every OrderX key is minted into. Lighter reserves
// indices 0-3 for its own web/mobile/desktop clients
// (https://apidocs.lighter.xyz/docs/api-keys): logging into app.lighter.xyz
// re-registers slot 0, which silently overwrote keys we used to mint there and
// got the account auto-disabled on the next signed request. 8 also stays clear
// of 3-7, the range the lighter-python example mints and bots copy.
export const ORDERX_API_KEY_INDEX = 8

// Adding a not-yet-selected index (at ORDERX_API_KEY_INDEX) starts it at
// `{ type: 'none' }`; toggling an already-selected index removes it entirely. `alreadyLinked`
// rows are refused, same guard Derive/Hyperliquid's pickers apply — a
// caller who somehow calls this on one of those returns state unchanged.
export function toggleSlot(
  state: SelectionState,
  accountIndex: string,
  info: { alreadyLinked: boolean },
  maxSlots: number
): SelectionState {
  if (info.alreadyLinked) return state
  const existingIndex = state.slots.findIndex((s) => s.accountIndex === accountIndex)
  if (existingIndex !== -1) {
    return { slots: state.slots.filter((_, i) => i !== existingIndex) }
  }
  if (state.slots.length >= maxSlots) return state
  return {
    slots: [
      ...state.slots,
      { accountIndex, apiKeyIndex: ORDERX_API_KEY_INDEX, keySource: { type: 'none' } },
    ],
  }
}

function updateSlot(
  state: SelectionState,
  accountIndex: string,
  keySource: KeySource
): SelectionState {
  return {
    slots: state.slots.map((s) => (s.accountIndex === accountIndex ? { ...s, keySource } : s)),
  }
}

export function setPreparing(state: SelectionState, accountIndex: string): SelectionState {
  return updateSlot(state, accountIndex, { type: 'preparing' })
}

export function setPrepared(
  state: SelectionState,
  accountIndex: string,
  preparedKeyId: string,
  personalSignText: string
): SelectionState {
  return updateSlot(state, accountIndex, { type: 'prepared', preparedKeyId, personalSignText })
}

export function setConfirming(
  state: SelectionState,
  accountIndex: string,
  preparedKeyId: string
): SelectionState {
  return updateSlot(state, accountIndex, { type: 'confirming', preparedKeyId })
}

export function setConfirmed(
  state: SelectionState,
  accountIndex: string,
  preparedKeyId: string
): SelectionState {
  return updateSlot(state, accountIndex, { type: 'confirmed', preparedKeyId })
}

// Resets a slot's key source back to 'none' — e.g. after a failed wallet
// mint, so the row falls back to the mint chooser.
export function setNone(state: SelectionState, accountIndex: string): SelectionState {
  return updateSlot(state, accountIndex, { type: 'none' })
}

// A slot is submittable once its key is a wallet-confirmed mint —
// 'none'/'preparing'/'prepared'/'confirming' all mean the customer hasn't
// finished resolving that slot's key yet.
export function isSlotResolved(slot: SlotSelection): boolean {
  return slot.keySource.type === 'confirmed'
}

// Authoritative submit gate: at least one slot selected, and every selected
// slot's key resolved. Unlike Derive there's no separate self-slot/sibling
// split to compute here — every Lighter slot (including the first) either
// links the initiating account or spawns a sibling, decided server-side by
// the link request's slot order (docs/lighter-frontend.md §2), not by any
// client-computed flag.
export function canSubmit(state: SelectionState): boolean {
  return state.slots.length >= 1 && state.slots.every(isSlotResolved)
}
